import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { Validators, FormGroup } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import * as fromStore from '../store/reducers';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import * as ruleActions from '../store/actions';
import { RuleParameterModel } from '../models/RuleParameterModel';
import { RuleListModel } from '../models/RuleListModel';
import { RuleModel } from '../models/RuleModel';
import { RuleCommonParameterModel } from '../models/RuleCommonParameterModel';
import { UpdateRuleParameterModel } from '../models/UpdateRuleParameterModel';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { AutoSaveFormControl } from '../../pillar/workspace/AutoSaveFormControl';
import { FormControlAutoSaveComponent } from '../../pillar/workspace/components/form-control-auto-save.component';
import { RuleService } from '../../businessRule/services/RuleService';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';

@Component({
  selector: 'brd-rule-workspace',
  templateUrl: './rule-workspace.component.html',
  styleUrls: ['./rule-workspace.component.scss']
})
export class RuleWorkspaceComponent implements OnInit, OnDestroy {
  destroyed$: Subject<boolean> = new Subject<boolean>();
  profileForm: FormGroup;
  parametersForm: FormGroup;
  commonParametersForm: FormGroup;
  ruleModel = new RuleModel;
  ruleCommonParameterModel = new RuleCommonParameterModel;
  commonParameters: RuleParameterModel[];
  customizedParameters: RuleParameterModel[];
  dataSets: string[];
  comparisonDates: string[];
  enableRuleEffectivenessConfiguration = false;
  loading = false;
  hasChange: boolean = false;

  editPermission: string = 'RuleModule.BusinessRule:Edit';
  enableDisablePermission: string = 'RuleModule.BusinessRule:EnableDisable';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  @Input()
  rule: RuleListModel;

  @Output()
  backClicked = new EventEmitter<boolean>();

  constructor(private store: Store<any>,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private ruleService: RuleService,
    private appConfigurationService: AppConfigurationService,
    private changeDetectorRef: ChangeDetectorRef ) {
      this.enableRuleEffectivenessConfiguration = appConfigurationService.configuration.enableRuleEffectivenessConfiguration;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  ngOnInit() {
    var ruleListString = sessionStorage.getItem("SelectedRule");
    if (ruleListString) {
      this.rule = JSON.parse(ruleListString);
    }
    this.commonParameters = this.rule.ruleParameters.filter(p => p.key.startsWith("CommonParameter_"));
    this.customizedParameters = this.rule.ruleParameters.filter(p => this.commonParameters.indexOf(p) < 0);

    this.actions$.ofType(ruleActions.DISABLE_RULE_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.alerts.addSuccess('Disable Rule Success');
        this.rule.isEnabled = false;
        sessionStorage.setItem("SelectedRule", JSON.stringify(this.rule));
        this.changeDetectorRef.markForCheck();
      });
  
    this.actions$.ofType(ruleActions.ENABLE_RULE_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.alerts.addSuccess('Enable Rule Success');
        this.rule.isEnabled = true;
        sessionStorage.setItem("SelectedRule", JSON.stringify(this.rule));
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(ruleActions.UPDATE_RULE_PARAMETER_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: ruleActions.UpdateRuleParameterSuccessAction) => {
        a.payload.control.saveSuccess(a.payload.parameter);
        var parameter = this.customizedParameters.find(p => p.key === a.payload.parameter.key);
        if (parameter) {
          parameter.value = a.payload.parameter.value;
        }
        this.changeDetectorRef.markForCheck();
      });

      this.actions$.ofType(ruleActions.GET_RELATED_DATASETS_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: ruleActions.GetDataSetsSuccessAction) => {
        this.dataSets = a.payload.model;
        if (this.ruleCommonParameterModel.comparisonDate) {
          this.ruleCommonParameterModel.dataSetName = this.ruleCommonParameterModel.dataSetName;
          this.store.dispatch(new ruleActions.GetComparisonDateAction({ model: this.ruleCommonParameterModel }));
        }
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(ruleActions.GET_COMPARISON_DATE_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: ruleActions.GetComparisonDateSuccessAction) => {
        this.comparisonDates = a.payload.model;
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(ruleActions.APPLY_COMMON_PARAMETERS_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: ruleActions.ApplyCommonParametersSuccessAction) => {
        if (!a.payload.model) {
          this.alerts.addError("Error applying common parameters");
        }
        else {
          this.alerts.addSuccess ("Parameters applied");
        }
        this.loading = false;
        this.hasChange = false;
        this.ruleCommonParameterModel = a.payload.model;
        this.changeDetectorRef.markForCheck();
      });

    this.ruleModel.dataSetName = this.rule.moduleDisplayName.replace(/\s/g, "");
    this.store.dispatch(new ruleActions.GetDataSetsAction ({model: this.ruleModel}));

    this.initForms();
    this.loadCommonParameters();
  }

  initForms(): void {
    this.profileForm = this.formBuilder.group({
      ruleDisplayName: this.formBuilder.control({ value: null, disabled: true },
        Validators.required,
        null,
        this.destroyed$,
        null,
        this.changeDetectorRef)
    });
    this.commonParametersForm = this.formBuilder.group({
      dataSet: [''],
      comparisonDate: [''],
      effectiveDate: null,
      expirationDate: null
      },
      null,
      this.destroyed$,
      (control) => {
        if (control.value.dataSet != this.ruleCommonParameterModel.dataSetName) {
          this.ruleCommonParameterModel.dataSetName = control.value.dataSet;
          this.store.dispatch(new ruleActions.GetComparisonDateAction({ model: this.ruleCommonParameterModel }));
        }
        this.ruleCommonParameterModel.dataSetName = control.value.dataSet;
        this.ruleCommonParameterModel.rule = this.rule;
        this.ruleCommonParameterModel.comparisonDate = control.value.comparisonDate;
        this.ruleCommonParameterModel.effectiveDate = control.value.effectiveDate;
        this.ruleCommonParameterModel.expirationDate = control.value.expirationDate;
      },
      this.changeDetectorRef);
    this.parametersForm = this.formBuilder.group({
      ruleParameters: this.formBuilder.array(this.rule.ruleParameters.map(p => this.initParameter()))
    });

    this.commonParametersForm.valueChanges.subscribe(value => {
      if (this.commonParametersForm.dirty) {
        this.hasChange = true;
      }
    });

    this.profileForm.patchValue(this.rule);
    this.profileForm.markAsPristine();
    this.commonParametersForm.patchValue(this.ruleCommonParameterModel);
    this.commonParametersForm.markAsPristine();
    this.parametersForm.patchValue(this.rule);
    this.parametersForm.markAsPristine();
  }

  initParameter() {
    return this.formBuilder.group({
      key: ['', Validators.required],
      value: this.formBuilder.control('', Validators.required)
    },
      null,
      this.destroyed$,
      (control) => {
        var parameter = control.value as RuleParameterModel;
        parameter.key = control.controls.key.value;
        this.store.dispatch(new ruleActions.UpdateRuleParameterAction({ rule: this.rule, parameter: parameter, control: control }));
      });
  }

  toggleEnabled(rule: RuleListModel) {
    if (!this.rule.isEnabled) {
      this.store.dispatch(new ruleActions.EnableRuleAction({ rule: this.rule }));
    } else {
      this.store.dispatch(new ruleActions.DisableRuleAction({ rule: this.rule }));
    }
  }

  apply() {
    if (this.invalidDates()) {
      this.alerts.addError('The expiration date must be later than the effective date');
    }
    else if (this.commonParametersForm.valid) {
      this.loading = true;
      this.store.dispatch(new ruleActions.ApplyCommonParametersAction({ model: this.ruleCommonParameterModel }));
    } else {
      this.loading = false;
      this.alerts.addError('Invalid rule parameter(s)');
    }
  }

  loadCommonParameters() {
    var dataSet = this.commonParameters.find(p => p.key === "CommonParameter_DataSetName");
    var comparisonDate = this.commonParameters.find(p => p.key === "CommonParameter_ComparisonDate");
    var effectiveDate = this.commonParameters.find(p => p.key === "CommonParameter_EffectiveDate");
    var expirationDate = this.commonParameters.find(p => p.key === "CommonParameter_ExpirationDate");
    this.ruleCommonParameterModel.rule = this.rule;
    this.ruleCommonParameterModel.dataSetName = dataSet ? dataSet.value : null;
    this.ruleCommonParameterModel.comparisonDate = comparisonDate ? comparisonDate.value : null;
    this.ruleCommonParameterModel.effectiveDate = effectiveDate ? new Date(effectiveDate.value) : null;
    this.ruleCommonParameterModel.expirationDate = expirationDate ? new Date(expirationDate.value) : null;
    this.commonParametersForm.controls['dataSet'].setValue(this.ruleCommonParameterModel.dataSetName);
    this.commonParametersForm.controls['comparisonDate'].setValue(this.ruleCommonParameterModel.comparisonDate);
    this.commonParametersForm.controls['effectiveDate'].setValue(this.ruleCommonParameterModel.effectiveDate);
    this.commonParametersForm.controls['expirationDate'].setValue(this.ruleCommonParameterModel.expirationDate);
  }

  isCommonParameter(parameter) {
    if (parameter.value.key != null) {
      return parameter.value.key.startsWith("CommonParameter_");
    }
    return false;
  }

  invalidDates() {
    var effectiveDate = this.ruleCommonParameterModel.effectiveDate;
    var expirationDate = this.ruleCommonParameterModel.expirationDate;
    return effectiveDate != null && expirationDate != null && effectiveDate > expirationDate;
  }

  back() {
    this.backClicked.emit(true);
  }

  dateChange (event: any, datePickerName: string) {
    switch ( datePickerName ) {
      case "effectiveDate": {
        this.ruleCommonParameterModel.effectiveDate = event.value;
        break;
      }
      case "expirationDate":{
        this.ruleCommonParameterModel.expirationDate = event.value;
        break;
      }
      default:{
        break;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/businessRule/components/rule-workspace.component.ts